/**
 * PaperPacketIntakePanel — Option A confirm surface + Option B OCR pre-fill.
 * Save uses existing medical-profile PATCH + allergy CRUD only.
 * OCR never writes the record.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, Grid,
  IconButton, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import SaveIcon from '@mui/icons-material/Save';
import { studentAPI } from '../../services/api';
import {
  CONDITION_FIELDS,
  EMPTY_ALLERGY,
  EMPTY_PROFILE,
  INTAKE_SOURCE_HEADER,
  INTAKE_SOURCE_PAPER_OCR,
  OCR_LOW_CONFIDENCE,
  SEVERITY_OPTIONS,
} from './medicalSafetyFields';

function FieldBadge({ meta }) {
  if (!meta) return null;
  const low = (meta.confidence ?? 1) < OCR_LOW_CONFIDENCE;
  return (
    <Chip
      size="small"
      label={low ? `OCR · review (${Math.round((meta.confidence || 0) * 100)}%)` : 'OCR suggestion'}
      color={low ? 'warning' : 'default'}
      variant={low ? 'filled' : 'outlined'}
      sx={{ ml: 1, height: 22, fontSize: '0.7rem' }}
    />
  );
}

function ocrFieldSx(meta) {
  if (!meta) return undefined;
  const low = (meta.confidence ?? 1) < OCR_LOW_CONFIDENCE;
  return {
    '& .MuiOutlinedInput-root': {
      bgcolor: low ? 'rgba(237, 108, 2, 0.06)' : 'rgba(25, 118, 210, 0.04)',
    },
  };
}

export default function PaperPacketIntakePanel({
  open,
  onClose,
  studentId,
  documents = [],
  initialDocumentId = null,
  onSaved,
}) {
  const [docId, setDocId] = useState(initialDocumentId || '');
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [docList, setDocList] = useState(documents || []);
  const [profile, setProfile] = useState(EMPTY_PROFILE());
  const [allergies, setAllergies] = useState([]);
  const [fieldMeta, setFieldMeta] = useState({});
  const [allergyMeta, setAllergyMeta] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const intakeHeaders = useMemo(
    () => ({ [INTAKE_SOURCE_HEADER]: INTAKE_SOURCE_PAPER_OCR }),
    [],
  );

  useEffect(() => {
    setDocList(documents || []);
  }, [documents]);

  const pdfDocs = useMemo(
    () => (docList || []).filter((d) => {
      const name = (d.file || d.title || '').toLowerCase();
      return name.endsWith('.pdf') || (d.file || '').toLowerCase().includes('.pdf');
    }),
    [docList],
  );

  // Default to newest PDF when opening; fetch docs if parent list empty
  useEffect(() => {
    if (!open || !studentId) return;
    let cancelled = false;
    async function ensureDocs() {
      let list = documents || [];
      if (!list.length) {
        try {
          const res = await studentAPI.getDocuments(studentId);
          list = Array.isArray(res.data) ? res.data : res.data?.results || [];
          if (!cancelled) setDocList(list);
        } catch {
          if (!cancelled) setDocList([]);
        }
      }
      const preferred = initialDocumentId
        || (list.find((d) => (d.file || '').toLowerCase().includes('.pdf')) || list[0] || {}).id
        || '';
      if (!cancelled) setDocId(preferred || '');
    }
    ensureDocs();
    return () => { cancelled = true; };
  }, [open, initialDocumentId, documents, studentId]);

  const loadProfile = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError('');
    try {
      const [profRes, allergyRes] = await Promise.all([
        studentAPI.getMedicalProfile(studentId),
        studentAPI.getAllergies(studentId),
      ]);
      setProfile({ ...EMPTY_PROFILE(), ...(profRes.data || {}) });
      setAllergies(Array.isArray(allergyRes.data) ? allergyRes.data : []);
      setFieldMeta({});
      setAllergyMeta([]);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load medical profile.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (open) loadProfile();
  }, [open, loadProfile]);

  // PDF blob for iframe
  useEffect(() => {
    let revoked = false;
    let url = '';
    async function loadPdf() {
      if (!open || !studentId || !docId) {
        setPdfUrl('');
        return;
      }
      setPdfLoading(true);
      try {
        url = await studentAPI.getDocumentObjectUrl(studentId, docId);
        if (!revoked) setPdfUrl(url);
      } catch (e) {
        if (!revoked) {
          setPdfUrl('');
          setError(e.response?.data?.detail || 'Could not load document PDF.');
        }
      } finally {
        if (!revoked) setPdfLoading(false);
      }
    }
    loadPdf();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, studentId, docId]);

  const updateProfileField = (key, value) => {
    setProfile((prev) => ({ ...prev, [key]: value }));
    setFieldMeta((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateAllergyField = (index, key, value) => {
    setAllergies((prev) => prev.map((a, i) => (i === index ? { ...a, [key]: value } : a)));
    setAllergyMeta((prev) => {
      if (!prev[index]) return prev;
      const next = [...prev];
      const row = { ...(next[index] || {}) };
      delete row[key];
      next[index] = row;
      return next;
    });
  };

  const addAllergy = () => {
    if (allergies.length >= 5) return;
    setAllergies((prev) => [...prev, EMPTY_ALLERGY(prev.length + 1)]);
    setAllergyMeta((prev) => [...prev, {}]);
  };

  const removeAllergy = async (index) => {
    const allergy = allergies[index];
    if (allergy?.id && !allergy._new) {
      try {
        await studentAPI.deleteAllergy(studentId, allergy.id, { headers: intakeHeaders });
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to delete allergy.');
        return;
      }
    }
    setAllergies((prev) => prev.filter((_, i) => i !== index));
    setAllergyMeta((prev) => prev.filter((_, i) => i !== index));
  };

  const applySuggestions = (payload) => {
    const fields = payload.fields || {};
    const nextProfile = { ...profile };
    const nextMeta = {};
    Object.entries(fields).forEach(([key, sug]) => {
      if (!sug || sug.value === undefined || sug.value === null) return;
      nextProfile[key] = sug.value;
      nextMeta[key] = { confidence: sug.confidence, source: sug.source };
    });
    setProfile(nextProfile);
    setFieldMeta(nextMeta);

    const sugAllergies = payload.allergies || [];
    if (sugAllergies.length) {
      const rows = sugAllergies.slice(0, 5).map((row, i) => {
        const base = EMPTY_ALLERGY(i + 1);
        const meta = {};
        ['allergen', 'reaction_description', 'requires_anaphylaxis_plan', 'reacts_to_eat', 'reacts_to_touch', 'reacts_to_smell', 'severity'].forEach((k) => {
          const sug = row[k];
          if (sug && sug.value !== undefined && sug.value !== null) {
            base[k] = sug.value;
            meta[k] = { confidence: sug.confidence, source: sug.source };
          }
        });
        return { row: base, meta };
      });
      setAllergies(rows.map((r) => r.row));
      setAllergyMeta(rows.map((r) => r.meta));
    }
  };

  const runOcr = async () => {
    if (!docId) {
      setError('Select a PDF document first.');
      return;
    }
    setOcrRunning(true);
    setError('');
    setInfo('');
    try {
      const res = await studentAPI.ocrMedicalSuggestions(studentId, docId);
      applySuggestions(res.data || {});
      setInfo('OCR suggestions applied — review flagged fields, then Save. Nothing was written yet.');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      if (status === 503 || e.response?.data?.ocr_configured === false) {
        setInfo('OCR is not configured on this environment. Enter values manually from the scan (Option A).');
      } else {
        setError(detail || 'OCR failed.');
      }
    } finally {
      setOcrRunning(false);
    }
  };

  const saveAll = async () => {
    setSaving(true);
    setError('');
    try {
      await studentAPI.updateMedicalProfile(studentId, profile, { headers: intakeHeaders });
      for (let i = 0; i < allergies.length; i++) {
        const a = allergies[i];
        const payload = {
          allergen: a.allergen,
          severity: a.severity || '',
          reacts_to_eat: !!a.reacts_to_eat,
          reacts_to_touch: !!a.reacts_to_touch,
          reacts_to_smell: !!a.reacts_to_smell,
          reaction_description: a.reaction_description || '',
          requires_anaphylaxis_plan: !!a.requires_anaphylaxis_plan,
          order: i + 1,
        };
        if (a.id && !a._new) {
          await studentAPI.updateAllergy(studentId, a.id, payload, { headers: intakeHeaders });
        } else if ((a.allergen || '').trim()) {
          await studentAPI.createAllergy(studentId, payload, { headers: intakeHeaders });
        }
      }
      const allergyRes = await studentAPI.getAllergies(studentId);
      setAllergies(Array.isArray(allergyRes.data) ? allergyRes.data : []);
      setFieldMeta({});
      setAllergyMeta([]);
      setInfo('Saved to Medical & Safety profile.');
      if (onSaved) onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { height: '92vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}>
        Paper packet intake
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Transcribe or OCR-prefill · Save writes the Medical & Safety profile only
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {info && <Alert severity="info" onClose={() => setInfo('')}>{info}</Alert>}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Document PDF</InputLabel>
            <Select
              label="Document PDF"
              value={docId || ''}
              onChange={(e) => setDocId(e.target.value)}
            >
              {(pdfDocs.length ? pdfDocs : docList).map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {(d.title || d.file || `Document ${d.id}`).toString().slice(0, 80)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={ocrRunning ? <CircularProgress size={16} /> : <DocumentScannerIcon />}
            onClick={runOcr}
            disabled={!docId || ocrRunning || loading}
          >
            {ocrRunning ? 'Running OCR…' : 'Run OCR'}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: '#f5f5f5' }}>
            {pdfLoading && (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
            )}
            {!pdfLoading && pdfUrl && (
              <iframe title="Paper packet PDF" src={pdfUrl} style={{ width: '100%', height: '100%', border: 0, minHeight: 520 }} />
            )}
            {!pdfLoading && !pdfUrl && (
              <Typography color="text.secondary" sx={{ p: 3 }}>
                Select a PDF document to preview. Upload a scanned packet on the Documents tab first if none appear.
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', pr: 1 }}>
            {loading ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
            ) : (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Medical profile</Typography>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Conditions</Typography>
                <Grid container spacing={0}>
                  {CONDITION_FIELDS.map((f) => (
                    <Grid item xs={6} sm={4} key={f.key}>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={!!profile?.[f.key]}
                            onChange={(e) => updateProfileField(f.key, e.target.checked)}
                            size="small"
                          />
                        )}
                        label={(
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2">{f.label}</Typography>
                            <FieldBadge meta={fieldMeta[f.key]} />
                          </Box>
                        )}
                      />
                    </Grid>
                  ))}
                </Grid>

                {[
                  ['medications_text', 'Medications', 2],
                  ['other_medical_notes', 'Other medical notes', 2],
                  ['food_from_home', 'Food from home', 1],
                  ['liquid_type', 'Liquid type', 1],
                  ['emergency_action_plan', 'Emergency action plan', 3],
                ].map(([key, label, rows]) => (
                  <TextField
                    key={key}
                    label={(
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        {label}
                        <FieldBadge meta={fieldMeta[key]} />
                      </Box>
                    )}
                    fullWidth
                    multiline={rows > 1}
                    rows={rows}
                    margin="normal"
                    size="small"
                    value={profile?.[key] || ''}
                    onChange={(e) => updateProfileField(key, e.target.value)}
                    sx={ocrFieldSx(fieldMeta[key])}
                  />
                ))}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Allergies</Typography>
                {allergies.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No allergies yet.</Typography>
                )}
                {allergies.map((a, idx) => (
                  <Box key={a.id || `new-${idx}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TextField
                        label="Allergen"
                        size="small"
                        value={a.allergen || ''}
                        onChange={(e) => updateAllergyField(idx, 'allergen', e.target.value)}
                        sx={{ flex: 1, ...ocrFieldSx(allergyMeta[idx]?.allergen) }}
                      />
                      <FieldBadge meta={allergyMeta[idx]?.allergen} />
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Severity</InputLabel>
                        <Select
                          label="Severity"
                          value={a.severity || ''}
                          onChange={(e) => updateAllergyField(idx, 'severity', e.target.value)}
                        >
                          {SEVERITY_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton size="small" color="error" onClick={() => removeAllergy(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      {['reacts_to_eat', 'reacts_to_touch', 'reacts_to_smell'].map((k) => (
                        <FormControlLabel
                          key={k}
                          control={(
                            <Checkbox
                              size="small"
                              checked={!!a[k]}
                              onChange={(e) => updateAllergyField(idx, k, e.target.checked)}
                            />
                          )}
                          label={<Typography variant="body2">{k.replace('reacts_to_', '')}</Typography>}
                        />
                      ))}
                    </Box>
                    <FormControlLabel
                      sx={{ mb: 1, display: 'block' }}
                      control={(
                        <Checkbox
                          size="small"
                          checked={!!a.requires_anaphylaxis_plan}
                          onChange={(e) => updateAllergyField(idx, 'requires_anaphylaxis_plan', e.target.checked)}
                        />
                      )}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2">
                            Food allergy / anaphylaxis risk — requires emergency plan (form_16)
                          </Typography>
                          <FieldBadge meta={allergyMeta[idx]?.requires_anaphylaxis_plan} />
                        </Box>
                      )}
                    />
                    <TextField
                      label="Reaction description"
                      fullWidth
                      size="small"
                      value={a.reaction_description || ''}
                      onChange={(e) => updateAllergyField(idx, 'reaction_description', e.target.value)}
                      sx={ocrFieldSx(allergyMeta[idx]?.reaction_description)}
                    />
                  </Box>
                ))}
                {allergies.length < 5 && (
                  <Button startIcon={<AddIcon />} onClick={addAllergy} size="small">Add allergy</Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={saveAll}
          disabled={saving || loading}
        >
          {saving ? 'Saving…' : 'Save to Medical & Safety'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
